import { privateApi } from 'api'
import { AxiosResponse } from 'axios'
import {
  AiEnhancementResponse,
  EnhanceBulkPayload,
  EnhanceCharacteristicsPayload,
  EnhanceDescriptionPayload,
  EnhanceImagePayload,
  EnhancementListResponse,
  EnhancementResponse,
  GetEnhancementDto,
  IActionsProductRequest,
  IActionsProductResponse,
  ICategory,
  ICategoryQueryRequest,
  ICategoryWithSelectedSellers,
  ICreateCategoryRequest,
  ICreateProduct,
  ICreateProductVariation,
  ICreateSubCategoryRequest,
  IEnhanceProductRequest,
  IEnhanceProductResponse,
  IEnhancementImageVariant,
  IGetSubCategoryCharacteristicsResponse,
  ILatestEnhancementDescription,
  IParsedCategory,
  IParentAndVariationsResponse,
  IPriceHistoryResponse,
  IProduct,
  IProductQueryRequest,
  IProductQueryResponse,
  IProductResponse,
  IQuickUpdateProductRequest,
  ISubCategory,
  ITopProductSalesQueryRequest,
  ITopProductSalesResponse,
  IUpdateCategoryRequest,
  IUpdateProduct,
  IUpdateSelectedSellersRequest,
  IUpdateSubCategoryCharacteristicsRequest,
  IUpdateSubCategoryRequest,
  TEnhanceProductImageListType
} from 'models/product'
import { getNormalizedFormData } from 'shared/utils/getNormalizedFormData'

export const productApi = {
  getProducts({
    skip,
    limit,
    all_fields,
    name,
    seller_id,
    category_id,
    subCategory_id,
    status,
    type,
    isHaveParent,
    excludeProductId,
    search_by,
    sort_by,
    sort_order,
    group,
    subCategoryOrEmpty_id,
    feed_only
  }: IProductQueryRequest): Promise<AxiosResponse<IProductQueryResponse>> {
    return privateApi.get<IProductQueryResponse>(
      `admin/products?skip=${skip}&limit=${limit}&all_fields=${
        all_fields ?? ''
      }&name=${name ?? ''}&seller_id=${seller_id ?? ''}&category_id=${
        category_id ?? ''
      }&subCategory_id=${subCategory_id ?? ''}&status=${status ?? ''}&type=${
        type ?? ''
      }&isHaveParent=${isHaveParent ?? ''}&excludeProductId=${
        excludeProductId ?? ''
      }&search_by=${search_by ?? ''}&sort_by=${sort_by ?? ''}&sort_order=${
        sort_order ?? ''
      }&group=${group ?? ''}&subCategoryOrEmpty_id=${
        subCategoryOrEmpty_id ?? ''
      }&feed_only=${feed_only ?? ''}`
    )
  },
  getProduct(id: string): Promise<AxiosResponse<IProductResponse>> {
    return privateApi.get<IProductResponse>(`admin/products/${id}`)
  },
  setActions({
    toCopy,
    toDelete,
    toDraft,
    toPublish,
    toReject,
    toBlock,
    toUnblock
  }: IActionsProductRequest): Promise<AxiosResponse<IActionsProductResponse>> {
    return privateApi.put(
      `admin/products?toCopy=${toCopy ?? ''}&toDelete=${
        toDelete ?? ''
      }&toDraft=${toDraft ?? ''}&toPublish=${toPublish ?? ''}&toReject=${
        toReject ?? ''
      }&toBlock=${toBlock ?? ''}&toUnblock=${toUnblock ?? ''}`
    )
  },
  getCategories({
    name,
    parsed
  }: ICategoryQueryRequest): Promise<
    AxiosResponse<IParsedCategory[] | ICategory[]>
  > {
    return privateApi.get(`admin/category?parsed=${parsed}&name=${name ?? ''}`)
  },
  createCategory(
    data: ICreateCategoryRequest
  ): Promise<AxiosResponse<ICategory>> {
    const formData = getNormalizedFormData(data)
    return privateApi.post<ICategory>('admin/category', formData)
  },
  updateCategory(
    categoryId: string,
    data: IUpdateCategoryRequest
  ): Promise<AxiosResponse<ICategory>> {
    const formData = getNormalizedFormData(data)
    return privateApi.patch<ICategory>(`admin/category/${categoryId}`, formData)
  },
  updateCategorySelectedSellers(
    categoryId: string,
    data: IUpdateSelectedSellersRequest
  ): Promise<AxiosResponse<ICategoryWithSelectedSellers>> {
    return privateApi.patch<ICategoryWithSelectedSellers>(
      `admin/category/${categoryId}/selected-sellers`,
      data
    )
  },
  getSubCategoriesByCategory(
    categoryId: string
  ): Promise<AxiosResponse<ISubCategory[]>> {
    return privateApi.get<ISubCategory[]>(`/admin/subCategory/${categoryId}`)
  },
  getAllSubCategories(
    skip: number,
    limit: number
  ): Promise<AxiosResponse<ISubCategory[]>> {
    return privateApi.get<ISubCategory[]>(
      `admin/subCategory?skip=${skip}&limit=${limit}`
    )
  },
  createSubCategory(
    data: ICreateSubCategoryRequest
  ): Promise<AxiosResponse<ISubCategory>> {
    return privateApi.post<ISubCategory>('admin/subCategory', data)
  },
  deleteSubCategory(subCategoryId: string): Promise<AxiosResponse> {
    return privateApi.delete(`admin/subCategory/${subCategoryId}`)
  },
  updateSubCategory(
    subCategoryId: string,
    data: IUpdateSubCategoryRequest
  ): Promise<AxiosResponse<ISubCategory>> {
    return privateApi.patch<ISubCategory>(
      `admin/subCategory/${subCategoryId}`,
      data
    )
  },
  toggleSubCategoryVisibility(
    subCategoryId: string,
    isVisible: boolean
  ): Promise<AxiosResponse<ISubCategory>> {
    return privateApi.patch<ISubCategory>(
      `admin/subCategory/toggle-visibility/${subCategoryId}`,
      {
        is_visible: isVisible
      }
    )
  },
  addProduct(data: ICreateProduct): Promise<AxiosResponse> {
    const formData = getNormalizedFormData(data)
    return privateApi.post('admin/products', formData)
  },
  updateProduct(data: IUpdateProduct): Promise<AxiosResponse<IProduct>> {
    const formData = getNormalizedFormData(data)
    return privateApi.patch<IProduct>('admin/products', formData)
  },
  replaceProductPhoto(
    productId: string,
    oldPhoto: string,
    file: File
  ): Promise<AxiosResponse<IProductResponse>> {
    const formData = new FormData()
    formData.append('oldPhoto', oldPhoto)
    formData.append('photo', file)

    return privateApi.patch<IProductResponse>(
      `admin/products/${productId}/photo`,
      formData
    )
  },
  quickUpdateProduct(
    id: string,
    data: IQuickUpdateProductRequest
  ): Promise<AxiosResponse<IProductResponse>> {
    return privateApi.patch<IProductResponse>(`admin/products/${id}/quick-update`, data)
  },
  createProductVariation(
    data: ICreateProductVariation
  ): Promise<AxiosResponse<IProduct>> {
    const formData = getNormalizedFormData(data)
    return privateApi.post<IProduct>('admin/products/variation', formData)
  },
  getMostPurchasedProducts({
    skip,
    limit,
    seller_id
  }: ITopProductSalesQueryRequest): Promise<
    AxiosResponse<ITopProductSalesResponse>
  > {
    return privateApi.get<ITopProductSalesResponse>(
      `admin/seller/products/top-sales?skip=${skip}&limit=${limit}&seller_id=${seller_id}`
    )
  },
  createAiImage(
    data: EnhanceImagePayload
  ): Promise<AxiosResponse<AiEnhancementResponse>> {
    return privateApi.post<AiEnhancementResponse>('enhance/image', data)
  },
  createAiCharacteristics(
    data: EnhanceCharacteristicsPayload
  ): Promise<AxiosResponse<AiEnhancementResponse>> {
    return privateApi.post<AiEnhancementResponse>(
      'enhance/characteristics',
      data
    )
  },
  createAiDescription(
    data: EnhanceDescriptionPayload
  ): Promise<AxiosResponse<AiEnhancementResponse>> {
    return privateApi.post<AiEnhancementResponse>('enhance/description', data)
  },
  enhanceProduct(
    data: IEnhanceProductRequest
  ): Promise<AxiosResponse<IEnhanceProductResponse>> {
    return privateApi.post<IEnhanceProductResponse>('enhance/product', data)
  },
  getLatestAiDescription(
    productId: string
  ): Promise<AxiosResponse<ILatestEnhancementDescription>> {
    return privateApi.get<ILatestEnhancementDescription>(
      `enhance/descriptions/${productId}/latest`
    )
  },
  getAiImages(
    productId: string,
    type: TEnhanceProductImageListType
  ): Promise<AxiosResponse<IEnhancementImageVariant[]>> {
    return privateApi.get<IEnhancementImageVariant[]>(
      `enhance/images/${productId}?type=${type}`
    )
  },
  getAiGoods({
    page,
    limit,
    sellerId
  }: GetEnhancementDto): Promise<AxiosResponse<EnhancementListResponse>> {
    const params = new URLSearchParams({
      page: (page ? page - 1 : 0).toString(),
      limit: limit?.toString() ?? '10',
      sellerId: sellerId ?? ''
    })
    return privateApi.get<EnhancementListResponse>(
      `enhance/products?${params.toString()}`
    )
  },
  deleteAiProducts(ids: string[]): Promise<AxiosResponse> {
    return privateApi.delete(`enhance/products/${ids.join(',')}`)
  },
  getAiProduct(id: string): Promise<AxiosResponse<EnhancementResponse>> {
    return privateApi.get<EnhancementResponse>(`enhance/product/${id}`)
  },
  rollbackDescription(id: string): Promise<AxiosResponse<IProductResponse>> {
    return privateApi.get<IProductResponse>(
      `admin/products/back-old-desc/${id}`
    )
  },
  saveAiProducts(ids: string[]): Promise<AxiosResponse> {
    return privateApi.post(`enhance/merge/${ids.join(',')}`)
  },
  removeProductImageBackground(
    data: EnhanceImagePayload
  ): Promise<AxiosResponse<AiEnhancementResponse>> {
    return privateApi.post<AiEnhancementResponse>(
      'enhance/removeBackground',
      data
    )
  },
  massAi(
    data: EnhanceBulkPayload
  ): Promise<AxiosResponse<AiEnhancementResponse>> {
    return privateApi.post<AiEnhancementResponse>('enhance/bulkEnhance', data)
  },
  getPriceHistory(id: string): Promise<AxiosResponse<IPriceHistoryResponse>> {
    return privateApi.get<IPriceHistoryResponse>(
      `admin/products/${id}/price-history`
    )
  },
  getParentAndVariations(
    id: string
  ): Promise<AxiosResponse<IParentAndVariationsResponse>> {
    return privateApi.get<IParentAndVariationsResponse>(
      `admin/products/parent-and-variations/${id}`
    )
  },
  makeProductIndependent(id: string): Promise<AxiosResponse<void>> {
    return privateApi.put<void>(`admin/products/make-product/${id}`)
  },
  toggleProductDisabled(
    id: string,
    disabled: boolean
  ): Promise<AxiosResponse<IProductResponse>> {
    return privateApi.patch<IProductResponse>(
      `admin/products/toggle-disabled/${id}`,
      { disabled }
    )
  },
  getSubCategoryCharacteristics(
    subCategoryId: string
  ): Promise<AxiosResponse<IGetSubCategoryCharacteristicsResponse>> {
    return privateApi.get<IGetSubCategoryCharacteristicsResponse>(
      `admin/sub-category-characteristic/${subCategoryId}`
    )
  },
  updateSubCategoryCharacteristics(
    subCategoryId: string,
    data: IUpdateSubCategoryCharacteristicsRequest
  ): Promise<AxiosResponse<IGetSubCategoryCharacteristicsResponse>> {
    return privateApi.put<IGetSubCategoryCharacteristicsResponse>(
      `admin/sub-category-characteristic/${subCategoryId}`,
      data
    )
  }
}
